import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  CircularProgress,
  IconButton,
  Tooltip,
  useMediaQuery,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import { ArrowBack, Download, Refresh } from "@mui/icons-material";
import { useGetEmpDataAdminQuery } from "state/api";
import * as XLSX from "xlsx";
import { useSelector } from "react-redux";

const RmDetails = () => {
  const { RmMgName } = useSelector((state) => state.auth);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedMonth, setSelectedMonth] = useState(() => {
    const stored = localStorage.getItem("selectedMonth");
    return stored ? parseInt(stored) : new Date().getMonth() + 1;
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    const stored = localStorage.getItem("selectedYear");
    return stored ? parseInt(stored) : new Date().getFullYear();
  });

  const [selectedProduct, setSelectedProduct] = useState("");
  const [selectedDay, setSelectedDay] = useState("");
  const [uniqueProducts, setUniqueProducts] = useState([]);

  console.log("Amdetails - RmMgName:", RmMgName);
  console.log("Amdetails - selectedMonth:", selectedMonth);
  console.log("Amdetails - selectedYear:", selectedYear);
  console.log("Amdetails - selectedProduct:", selectedProduct);
  console.log("Amdetails - selectedDay:", selectedDay);

  const {
    data: apiResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetEmpDataAdminQuery({
    RmMgName: RmMgName || "",
    product: selectedProduct || "",
    month: selectedMonth,
    year: selectedYear,
    day: selectedDay ? parseInt(selectedDay) : null,
  });

  // Extract the actual data array from the API response
  const data = apiResponse?.data || [];

  // Refetch when filters change
  useEffect(() => {
    if (RmMgName) {
      console.log("Refetching data for RmMgName:", RmMgName);
      refetch();
    }
  }, [
    RmMgName,
    selectedMonth,
    selectedYear,
    selectedProduct,
    selectedDay,
    refetch,
  ]);

  // Extract unique products from data
  useEffect(() => {
    const products = [...new Set(data.map((item) => item.product))]
      .filter(Boolean)
      .sort();
    setUniqueProducts(products);
  }, [data]);

  console.log("API Response Data:", apiResponse);
  console.log("Extracted Data Array:", data);
  console.log("API Error:", error);
  console.log(
    "API Error Message:",
    error?.data?.message || error?.message || "Unknown error"
  );
  console.log("Is Error:", isError);

  const handleExport = () => {
    const worksheet = XLSX.utils.json_to_sheet(data || []);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Daily Report");
    XLSX.writeFile(
      workbook,
      `${RmMgName}_${selectedMonth}_${selectedYear}.xlsx`
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#0f172a",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
          gap: 3,
          textAlign: "center",
          padding: 3,
          background: "#0f172a",
        }}
      >
        <Typography variant="h5" sx={{ color: "#ef4444" }}>
          Error loading data
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={refetch}
          sx={{ mt: 2, color: "#3b82f6", borderColor: "#3b82f6" }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: { xs: 2, md: 4 },
        maxWidth: "1800px",
        margin: "0 auto",
        minHeight: "100vh",
        background: "#0f172a",
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 4,
          flexWrap: "wrap",
        }}
      >
        <IconButton onClick={() => navigate(-1)} sx={{ color: "#f1f5f9" }}>
          <ArrowBack />
        </IconButton>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: "#f1f5f9",
            flexGrow: 1,
            fontSize: { xs: "1.5rem", md: "2rem" },
          }}
        >
          {RmMgName || "Medical Representative"} - Daily Report
        </Typography>

        <Button
          variant="contained"
          onClick={handleExport}
          startIcon={<Download />}
          sx={{
            py: 1,
            px: 3,
            borderRadius: 2,
            textTransform: "none",
            background: "#10b981",
            "&:hover": { background: "#059669" },
          }}
        >
          Export
        </Button>
      </Box>

      {/* Filter Section */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 4,
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <TextField
          label="Month"
          type="number"
          inputProps={{ min: 1, max: 12 }}
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          sx={{
            width: 100,
            "& .MuiOutlinedInput-root": {
              color: "white",
              "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
              "&:hover fieldset": { borderColor: "#3b82f6" },
            },
            "& .MuiInputBase-input": { color: "white" },
          }}
        />
        <TextField
          label="Year"
          type="number"
          value={selectedYear}
          onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          sx={{
            width: 120,
            "& .MuiOutlinedInput-root": {
              color: "white",
              "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
              "&:hover fieldset": { borderColor: "#3b82f6" },
            },
            "& .MuiInputBase-input": { color: "white" },
          }}
        />
        <TextField
          select
          label=""
          value={selectedProduct}
          onChange={(e) => setSelectedProduct(e.target.value)}
          SelectProps={{
            native: true,
          }}
          sx={{
            width: 150,
            "& .MuiOutlinedInput-root": {
              color: "white",
              "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
              "&:hover fieldset": { borderColor: "#3b82f6" },
            },
            "& .MuiInputBase-input": { color: "white" },
            "& .MuiSelect-icon": { color: "white" },
          }}
        >
          <option value="">All Products</option>
          {uniqueProducts.map((product) => (
            <option key={product} value={product}>
              {product}
            </option>
          ))}
        </TextField>
        <TextField
          select
          label=""
          value={selectedDay}
          onChange={(e) => setSelectedDay(e.target.value)}
          SelectProps={{
            native: true,
          }}
          sx={{
            width: 100,
            "& .MuiOutlinedInput-root": {
              color: "white",
              "& fieldset": { borderColor: "rgba(59, 130, 246, 0.5)" },
              "&:hover fieldset": { borderColor: "#3b82f6" },
            },
            "& .MuiInputBase-input": { color: "white" },
            "& .MuiSelect-icon": { color: "white" },
          }}
        >
          <option value="">All Days</option>
          {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
            <option key={day} value={day}>
              {day}
            </option>
          ))}
        </TextField>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => refetch()}
          sx={{ color: "#3b82f6", borderColor: "#3b82f6" }}
        >
          Refresh
        </Button>
      </Box>

      {/* Data Table */}
      {(data || []).length > 0 ? (
        <TableContainer
          component={Paper}
          sx={{
            background: "#1e293b",
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(59, 130, 246, 0.2)",
            border: "1px solid rgba(59, 130, 246, 0.2)",
          }}
        >
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow
                sx={{
                  background:
                    "linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)",
                  "& .MuiTableCell-head": {
                    color: "white",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                  },
                }}
              >
                <TableCell>RM Name</TableCell>
                <TableCell>MR Name</TableCell>
                <TableCell align="center">Date</TableCell>
                <TableCell align="center">Doctor</TableCell>
                <TableCell align="center">Product</TableCell>
                <TableCell align="center">Approved</TableCell>
                <TableCell align="center">Rejected</TableCell>
                <TableCell align="center">Pending</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data || []).map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor:
                      index % 2 === 0
                        ? "rgba(59, 130, 246, 0.05)"
                        : "transparent",
                    borderBottom: "1px solid rgba(59, 130, 246, 0.1)",
                    "&:hover": {
                      backgroundColor: "rgba(59, 130, 246, 0.1)",
                      transition: "all 0.3s ease",
                    },
                  }}
                >
                  <TableCell sx={{ color: "#f1f5f9", fontWeight: 600 }}>
                    {row.RmMgName}
                  </TableCell>
                  <TableCell sx={{ color: "#f1f5f9" }}>
                    {row.MrName || "-"}
                  </TableCell>
                  <TableCell align="center" sx={{ color: "#f1f5f9" }}>
                    {row.Date
                      ? new Date(row.Date).toLocaleDateString("en-IN")
                      : "-"}
                  </TableCell>
                  <TableCell align="center" sx={{ color: "#94a3b8" }}>
                    {row.DoctName || "-"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: row.product === "Finease" ? "#3b82f6" : "#ef4444",
                      fontWeight: 600,
                    }}
                  >
                    {row.product || "-"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ color: "#10b981", fontWeight: 700 }}
                  >
                    {row.Approved || 0}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ color: "#ef4444", fontWeight: 700 }}
                  >
                    {row.Rejected || 0}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ color: "#f59e0b", fontWeight: 700 }}
                  >
                    {row.Pending || 0}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "300px",
            textAlign: "center",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ color: "#94a3b8" }}>
            No records found for the selected period
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RmDetails;
